import React, { useEffect, useState } from "react"
import { Modal, Button, Select, Flex, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { CloseOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { getTripCategories, getTripSubcategories, seedTripSubcategories } from "store/trips"
import { api } from "store/helpers/api"
import styles from "./index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import CustomDrawer from "ui/Drawer"

// Same catalog and fields as the wizard's Details step: one category (category_id) plus at least
// one of that category's sub categories (subcategory_ids). Changing the category clears the sub
// categories, since they belong to the old one.
const EditCategoryModal = ({ visible, onClose, onSave, tripId, loading }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  const { data: categories, loading: isCategoriesLoading } = useSelector((state) => state.trips.tripCategories)
  const { data: subcategories, loading: isSubcategoriesLoading } = useSelector((state) => state.trips.tripSubcategories)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedSubcategories, setSelectedSubcategories] = useState([])
  const [isLoadingTrip, setIsLoadingTrip] = useState(false)

  useEffect(() => {
    if (visible && categories.length === 0) dispatch(getTripCategories())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // On open: fetch the trip's current category and sub categories and seed the selects from the
  // response, so nothing carries over from the previous open (the modal never unmounts).
  //
  // Direct api() (not getGuideTrip) so it (a) doesn't flip the shared trips.trip loading flag
  // the Save button watches, and (b) carries a `_t` cache-buster — the GET URL is otherwise
  // identical each open, so the browser would serve a stale body right after a save.
  useEffect(() => {
    if (!visible) return
    setSelectedCategory(null)
    setSelectedSubcategories([])
    if (!tripId) return

    let isActive = true
    setIsLoadingTrip(true)
    dispatch(api({ url: `/guide/trips/${tripId}`, params: { include_boats: false, _t: Date.now() } }))
      .then((result) => {
        if (!isActive) return
        const fullTrip = result?.data || result
        const categoryId = fullTrip?.category_id ?? null
        setSelectedCategory(categoryId)
        setSelectedSubcategories(fullTrip?.subcategory_ids || [])
        if (categoryId) dispatch(getTripSubcategories(categoryId))
        else dispatch(seedTripSubcategories([]))
      })
      .catch(() => {})
      .finally(() => {
        if (isActive) setIsLoadingTrip(false)
      })
    return () => {
      isActive = false
    }
  }, [visible, tripId, dispatch])

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId)
    setSelectedSubcategories([])
    if (categoryId) dispatch(getTripSubcategories(categoryId))
    else dispatch(seedTripSubcategories([]))
  }

  const handleSave = () => {
    onSave({ categoryId: selectedCategory, subcategoryIds: selectedSubcategories })
  }

  const handleCancel = () => {
    setSelectedCategory(null)
    setSelectedSubcategories([])
    onClose()
  }

  const isSaveDisabled = selectedCategory == null || selectedSubcategories.length === 0

  const formContent = (
    <>
      {isLoadingTrip ? (
        <Flex align="center" justify="center" className={styles.categoryLoading}>
          <Spin />
        </Flex>
      ) : (
        <>
          <div className={styles.formGroup}>
            <LabeledField labelKey={t("equipment.edit_category_modal.category_label")}>
              <Select
                placeholder={t("equipment.edit_category_modal.select_category_placeholder")}
                value={selectedCategory}
                onChange={handleCategoryChange}
                className={styles.select}
                size="large"
                showSearch
                loading={isCategoriesLoading}
                optionFilterProp="label"
                options={categories.map((category) => ({ value: category.id, label: category.name }))}
              />
            </LabeledField>
          </div>
          <div className={styles.formGroup}>
            <LabeledField labelKey={t("equipment.edit_category_modal.subcategory_label")}>
              <Select
                mode="multiple"
                placeholder={
                  selectedCategory
                    ? t("experiences.details.placeholders.select_tags")
                    : t("experiences.details.placeholders.select_category_first", "Select a category first")
                }
                value={selectedSubcategories}
                onChange={setSelectedSubcategories}
                className={styles.multiSelect}
                size="large"
                showSearch
                disabled={!selectedCategory}
                loading={isSubcategoriesLoading}
                optionFilterProp="label"
                options={subcategories.map((subcategory) => ({ value: subcategory.id, label: subcategory.name }))}
              />
            </LabeledField>
          </div>
        </>
      )}

      <Flex align="center" justify="space-between" gap={10} className={styles.editCategoryModalButtons}>
        <Button size="large" color="primary" variant="outlined" onClick={handleCancel} disabled={loading}>
          {t("equipment.edit_category_modal.cancel_button")}
        </Button>
        <Button size="large" color="primary" variant="solid" onClick={handleSave} disabled={isSaveDisabled} loading={loading}>
          {t("equipment.edit_category_modal.changes_button")}
        </Button>
      </Flex>
    </>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={visible} setOpen={handleCancel} title={t("equipment.edit_category_modal.title")}>
          <div className={styles.container}>{formContent}</div>
        </CustomDrawer>
      ) : (
        <Modal
          open={visible}
          onCancel={handleCancel}
          closeIcon={<CloseOutlined className={styles.closeIcon} />}
          footer={null}
          width={400}
          className={styles.modal}
          centered
          maskClosable={false}
        >
          <div className={styles.container}>
            <h2 className={styles.title}>{t("equipment.edit_category_modal.title")}</h2>
            {formContent}
          </div>
        </Modal>
      )}
    </>
  )
}

EditCategoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool
}

EditCategoryModal.defaultProps = {
  tripId: null,
  loading: false
}

export default EditCategoryModal
